import {
  doAsync,
  sessionStorageRemoveItem,
  sessionStorageSetItem,
} from '@aboutyou/utils';
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useRef,
} from 'react';
import type {StoreApi, UseBoundStore} from 'zustand';
import {createSharedStore} from '../sharedStore';
import {getIsConsentBannerAccepted} from '../utils/consentBannerAcceptedState';
import {getValueToPersist} from '../utils/getValueToPersist';

type StoredValueSlice = {
  value?: unknown;
  valueAfterCookieConsent?: unknown;
};

interface SessionStorageStoreState {
  store: Record<string, StoredValueSlice>;
  update: (slice: Record<string, StoredValueSlice>) => void;
}

export const EMPTY_VALUE = Symbol('EMPTY_VALUE');

type ContextValue = {
  useStoreBase: UseBoundStore<StoreApi<SessionStorageStoreState>>;
  updateSessionStorage: (options: {
    key: string;
    nextValue: unknown;
    forcePersistValue_ILLEGALLY?: boolean;
  }) => void;
};

const SessionStorageContext = createContext<ContextValue>(
  new Proxy<ContextValue>({} as ContextValue, {
    get() {
      throw new Error(
        'Cannot find value for SessionStorageContext. Did you add a SessionStorageProvider in one of the parent components?',
      );
    },
  }),
);

export const useStoredValue = (key: string) => {
  const storeValue = useContext(SessionStorageContext).useStoreBase(
    state => state.store[key],
  );
  return storeValue?.valueAfterCookieConsent ?? storeValue?.value;
};

export const useUpdateStoreValue = () => {
  const updateSessionStorage = useContext(
    SessionStorageContext,
  ).updateSessionStorage;
  return updateSessionStorage;
};

export function SessionStorageProvider({children}: PropsWithChildren) {
  const useStoreBase = useRef(
    createSharedStore<SessionStorageStoreState>(set => ({
      store: {},
      update: slice => {
        return set(state => ({store: {...state.store, ...slice}}));
      },
    })),
  ).current;

  const updateSessionStorage: ContextValue['updateSessionStorage'] =
    useCallback(
      async ({key, nextValue, forcePersistValue_ILLEGALLY}) => {
        const valueBeforeCookieConsent =
          useStoreBase.getState().store[key]?.value;

        if (nextValue === undefined) {
          sessionStorageRemoveItem(key);
          useStoreBase.getState().update({
            [key]: {value: EMPTY_VALUE, valueAfterCookieConsent: EMPTY_VALUE},
          });
          return;
        }
        const canPersist =
          getIsConsentBannerAccepted() || forcePersistValue_ILLEGALLY;

        const valueToStore: StoredValueSlice = {
          value: canPersist ? valueBeforeCookieConsent : nextValue,
          valueAfterCookieConsent: canPersist ? nextValue : undefined,
        };
        const currentValue = useStoreBase.getState().store[key];

        if (
          valueToStore.value === currentValue?.value &&
          valueToStore.valueAfterCookieConsent ===
            currentValue?.valueAfterCookieConsent
        ) {
          return;
        }

        const stateSlice = {[key]: valueToStore};
        useStoreBase.getState().update(stateSlice);

        if (canPersist) {
          doAsync(() => {
            sessionStorageSetItem(
              key,
              getValueToPersist({
                valueBeforeCookieConsent,
                valueAfterCookieConsent: valueToStore.valueAfterCookieConsent,
              }),
            );
          });
        }
      },
      [useStoreBase],
    );

  return (
    <SessionStorageContext.Provider
      value={{
        useStoreBase,
        updateSessionStorage,
      }}
    >
      {children}
    </SessionStorageContext.Provider>
  );
}
